//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { type ApiClient, backgroundPollHeaders } from './apiClient';
import { type Job, type GetJobsOptions, type JobsPage } from './apiTypes';

/**
 * Jobs and logs: status, listing, counts/stats, cancellation, and log fetches.
 */
export class JobsService {
  constructor(private readonly api: ApiClient) {}

  getJobStatus = (jobId: string, background = false): Promise<Job> => {
    return this.api.makeRequest<Job>(
      `job?job=${encodeURIComponent(jobId)}`, 'get', null, false, null,
      backgroundPollHeaders(background));
  };

  getJobs = (
    options: GetJobsOptions = {}
  ): Promise<JobsPage> => {
    const {
      job_status, offset = 0, limit = 50, start_date, end_date,
      description, user, dataset, min_runtime_s, max_runtime_s,
      min_num_rows, max_num_rows, min_job_id, max_job_id, newest_first,
      others_active, prioritize_active, background = false
    } = options;
    const params = new URLSearchParams();
    if (job_status)
      params.set('job_status', job_status);
    params.set('offset', String(offset));
    params.set('limit', String(limit));
    if (start_date)
      params.set('start_date', start_date);
    if (end_date)
      params.set('end_date', end_date);
    if (description)
      params.set('description', description);
    if (user)
      params.set('user', user);
    if (dataset)
      params.set('dataset', dataset);
    if (min_runtime_s != null)
      params.set('min_runtime_s', String(min_runtime_s));
    if (max_runtime_s != null)
      params.set('max_runtime_s', String(max_runtime_s));
    if (min_num_rows != null)
      params.set('min_num_rows', String(min_num_rows));
    if (max_num_rows != null)
      params.set('max_num_rows', String(max_num_rows));
    if (min_job_id != null)
      params.set('min_job_id', String(min_job_id));
    if (max_job_id != null)
      params.set('max_job_id', String(max_job_id));
    if (newest_first !== undefined)
      params.set('newest_first', String(newest_first));
    // Sent only to opt out; the server defaults to true.
    if (others_active === false)
      params.set('others_active', 'false');
    // Sent only to opt in; the server defaults to false (strict submit order).
    if (prioritize_active === true)
      params.set('prioritize_active', 'true');
    return this.api.makeRequest<JobsPage>(
      `jobs?${params.toString()}`, 'get', null, false, null,
      backgroundPollHeaders(background));
  };

  getJobCounts = (
    background = false, includeActiveOthers = true
  ): Promise<Record<string, number>> => {
    const params = new URLSearchParams();
    // Sent only to opt out; the server defaults to true.
    if (includeActiveOthers === false)
      params.set('others_active', 'false');
    const qs = params.toString();
    return this.api.makeRequest<Record<string, number>>(
      `jobs/counts${qs ? `?${qs}` : ''}`, 'get', null, false, null,
      backgroundPollHeaders(background));
  };

  getJobStats = (
    options: GetJobsOptions = {}
  ): Promise<{
    min_runtime_s: number; max_runtime_s: number; avg_runtime_s: number;
    most_common_status: string; latest_start_time: string | null;
    total: number; completed?: number; running?: number;
  }> => {
    const {
      job_status, start_date, end_date, description, user, dataset,
      min_runtime_s, max_runtime_s, min_num_rows, max_num_rows,
      others_active, background = false
    } = options;
    const params = new URLSearchParams();
    if (job_status) params.set('job_status', job_status);
    if (start_date) params.set('start_date', start_date);
    if (end_date) params.set('end_date', end_date);
    if (description) params.set('description', description);
    if (user) params.set('user', user);
    if (dataset) params.set('dataset', dataset);
    if (min_runtime_s != null)
      params.set('min_runtime_s', String(min_runtime_s));
    if (max_runtime_s != null)
      params.set('max_runtime_s', String(max_runtime_s));
    if (min_num_rows != null)
      params.set('min_num_rows', String(min_num_rows));
    if (max_num_rows != null)
      params.set('max_num_rows', String(max_num_rows));
    // Sent only to opt out; the server defaults to true.
    if (others_active === false)
      params.set('others_active', 'false');
    return this.api.makeRequest<{
      min_runtime_s: number; max_runtime_s: number; avg_runtime_s: number;
      most_common_status: string; latest_start_time: string | null;
      total: number; completed?: number; running?: number;
    }>(`jobs/stats?${params.toString()}`, 'get', null, false, null,
       backgroundPollHeaders(background));
  };

  cancelJob = async (jobId: string): Promise<void> => {
    await this.api.makeRequest(
      `canceljob?job=${encodeURIComponent(jobId)}`, 'get');
  };

  // Change a queued/running job's scheduling priority (1..40, higher runs
  // sooner).  The server enforces the privilege rules and rejects a
  // disallowed change (e.g. raising a job above normal without admin).
  setPriority = async (
    jobId: string | number, priority: number
  ): Promise<{ job_id: number; priority: number }> => {
    return this.api.makeRequest<{ job_id: number; priority: number }>(
      `setpriority?job=${encodeURIComponent(String(jobId))}`, 'post',
      { priority });
  };

  cancelJobs = (
    jobIds: (number | string)[]
  ): Promise<{
    requested: number;
    canceled: number;
    failed?: { job_id: number | string; error: string }[];
  }> => {
    return this.api.makeRequest<{
      requested: number;
      canceled: number;
      failed?: { job_id: number | string; error: string }[];
    }>('canceljobs', 'post', { job_ids: jobIds });
  };
}
